import { Box, SxProps, Typography } from '@mui/material';
import { IntlShape, useIntl } from 'react-intl';
import { useMemo } from 'react';
import parse from 'date-fns/parse';
import {
  faMars,
  faVenus,
  faVenusMars,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';

type PatientProfileProps = {
  patient: ProfilePatient;
  sx?: SxProps;
};

export type ProfilePatient = {
  id: number;
  gender: 'M' | 'F' | null;
  dateOfBirth?: string;
  strengths: string;
  communicationLevel: string;
  interactionLevel: string;
  selfCareAssistanceLevel: string;
};

export const PatientProfile = ({ patient, sx }: PatientProfileProps) => {
  const intl = useIntl();
  const info = useMemo(() => childInfo(patient, intl), [patient, intl]);
  const age = useMemo(() => {
    if (patient.dateOfBirth) {
      return localizeAge(
        parse(patient.dateOfBirth, 'yyyy-MM-dd', new Date()),
        new Date(),
        intl,
      );
    }
    return null;
  }, [patient.dateOfBirth, intl]);
  const genderIcon =
    patient.gender === 'M'
      ? faMars
      : patient.gender === 'F'
      ? faVenus
      : faVenusMars;
  return (
    <Box sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: 8, ...sx }}>
      <Box
        sx={{
          p: 5,
          backgroundColor: '#00b2a9',
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Typography color='primary.contrastText'>{age}</Typography>
        <Box>
          <FontAwesomeIcon icon={genderIcon} color='#ffffff' />
        </Box>
      </Box>
      <Box sx={{ p: 5 }}>
        <Typography>{info}</Typography>
      </Box>
    </Box>
  );
};

export const childInfo = (patient: ProfilePatient, intl: IntlShape) => {
  const sentences: string[] = [];

  if (patient.strengths != null) {
    sentences.push(patient.strengths);
  }

  let qualities: string[] = [];
  if (
    patient.communicationLevel != null &&
    patient.communicationLevel.trim() !== ''
  ) {
    qualities.push(
      intl.formatMessage({
        id: `profile.children.question.communication-level.${patient.communicationLevel}`,
      }),
    );
  }
  if (
    patient.interactionLevel != null &&
    patient.interactionLevel.trim() !== ''
  ) {
    qualities.push(
      intl.formatMessage({
        id: `profile.children.question.interaction-level.${patient.interactionLevel}`,
      }),
    );
  }
  if (
    patient.selfCareAssistanceLevel != null &&
    patient.selfCareAssistanceLevel.trim() != ''
  ) {
    qualities.push(
      intl.formatMessage({
        id: `profile.children.question.self-care-assistance-level.${patient.selfCareAssistanceLevel}`,
      }),
    );
  }
  if (qualities.length > 0) {
    const [first, ...rest] = qualities;
    qualities = [first, ...rest.map((s) => s.toLowerCase())];
    if (qualities.length == 1) {
      sentences.push(qualities[0]);
    } else if (qualities.length == 2) {
      sentences.push(`${qualities[0]} and ${qualities[1]}`);
    } else {
      const tail = qualities.pop();
      const head = qualities.join(', ');
      sentences.push(`${head}, and ${tail}`);
    }
  }

  return sentences.length == 0
    ? 'N/A'
    : sentences
        .filter(Boolean)
        .map((s) => (isEnglishSentence(s) ? s : `${s}.`))
        .join(' ');
};

const isEnglishSentence = (input: string) => {
  return (
    input !== null &&
    input !== '' &&
    (input.endsWith('.') || input.endsWith('!') || input.endsWith('?'))
  );
};

export const localizeAge = (birthDate: Date, now: Date, intl: IntlShape) => {
  const yearAge = ageInYear(birthDate, now);
  if (yearAge >= 1) {
    return intl.formatMessage(
      { id: 'profile.children.age-year' },
      { age: yearAge },
    );
  } else {
    return intl.formatMessage(
      { id: 'profile.children.age-month' },
      { age: ageInMonth(birthDate, now) },
    );
  }
};

export const ageInYear = (birthDate: Date, now: Date) =>
  Math.floor(ageInDay(birthDate, now) / 365);

export const ageInMonth = (birthDate: Date, now: Date) =>
  Math.floor(ageInDay(birthDate, now) / 30);

const ageInDay = (birthDate: Date, now: Date) =>
  differenceInCalendarDays(now, birthDate);
